/**
 * GoalReportsPage — Trackier-style goal performance report.
 *
 * Composes: PageHeader (date-range + export CSV), 4 KPI tiles
 * (goals tracked, completions, CVR, revenue), filter bar (metric + scope),
 * breakdown table (goal, completions, rate, payout/target) with loading /
 * empty / error states, and an inline SVG bar chart of completion rate by
 * goal. Pure SVG — no chart library dependency.
 *
 * Palette: green-only accent (green-600 / green-700 / green-50), white cards
 * with zinc-200 borders, zinc-900 text, Inter, 6-8px rounded corners.
 * No emoji, no dark mode, no competitor branding.
 */
import { useMemo, useState } from "react";
import { useGoalReportsQuery } from "./hooks";
import type {
	GoalMetric,
	GoalReportRow,
	GoalScope,
	GoalStatus,
} from "./api";

type MetricFilter = GoalMetric | "all";
type ScopeFilter = GoalScope | "all";

const METRIC_LABEL: Record<GoalMetric, string> = {
	conversions: "Conversions",
	revenue: "Revenue",
	clicks: "Clicks",
};

const STATUS_LABEL: Record<GoalStatus, string> = {
	on_track: "On track",
	at_risk: "At risk",
	off_track: "Off track",
	complete: "Complete",
};

const STATUS_CHIP: Record<GoalStatus, string> = {
	on_track: "bg-green-50 text-green-700 ring-1 ring-green-600/20",
	at_risk: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20",
	off_track: "bg-rose-50 text-rose-700 ring-1 ring-rose-600/20",
	complete: "bg-green-50 text-green-800 ring-1 ring-green-700/30",
};

function todayIso(): string {
	return new Date().toISOString().slice(0, 10);
}

function daysAgoIso(n: number): string {
	const d = new Date();
	d.setDate(d.getDate() - n);
	return d.toISOString().slice(0, 10);
}

function fmtInt(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return Math.round(n).toLocaleString();
}

function fmtPct(n: number): string {
	if (!Number.isFinite(n)) return "0.0%";
	return `${n.toFixed(1)}%`;
}

function fmtCurrency(n: number): string {
	if (!Number.isFinite(n)) return "$0.00";
	return `$${n.toLocaleString(undefined, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})}`;
}

function escapeCsv(v: string): string {
	if (/[",\n]/.test(v)) {
		return `"${v.replace(/"/g, '""')}"`;
	}
	return v;
}

function rowsToCsv(rows: GoalReportRow[]): string {
	const head = [
		"goal_id",
		"name",
		"metric",
		"target_value",
		"actual_total",
		"pct_complete",
		"status",
		"period_state",
		"days_elapsed",
		"days_total",
		"days_remaining",
		"projected_eot",
	];
	const lines = [head.join(",")];
	for (const r of rows) {
		lines.push(
			[
				escapeCsv(r.goal_id),
				escapeCsv(r.name),
				escapeCsv(r.metric),
				String(r.target_value),
				String(r.actual_total),
				r.pct_complete.toFixed(2),
				escapeCsv(r.status),
				escapeCsv(r.period_state),
				String(r.days_elapsed),
				String(r.days_total),
				String(r.days_remaining),
				String(r.projected_eot),
			].join(","),
		);
	}
	return lines.join("\n");
}

/**
 * Trigger a download of a CSV string. Runs only when the user clicks
 * Export, so SSR / test environments without URL.createObjectURL are safe
 * as long as we guard on its presence.
 */
function downloadCsv(filename: string, csv: string): void {
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
	const createUrl =
		typeof URL !== "undefined" && typeof URL.createObjectURL === "function"
			? URL.createObjectURL.bind(URL)
			: null;
	const revokeUrl =
		typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function"
			? URL.revokeObjectURL.bind(URL)
			: null;
	if (!createUrl) return;
	const url = createUrl(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	if (revokeUrl) revokeUrl(url);
}

function IconCalendar() {
	return (
		<svg
			aria-hidden
			width={14}
			height={14}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<rect x="3" y="4" width="18" height="18" rx="2" />
			<line x1="16" y1="2" x2="16" y2="6" />
			<line x1="8" y1="2" x2="8" y2="6" />
			<line x1="3" y1="10" x2="21" y2="10" />
		</svg>
	);
}

function IconDownload() {
	return (
		<svg
			aria-hidden
			width={14}
			height={14}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
			<polyline points="7 10 12 15 17 10" />
			<line x1="12" y1="15" x2="12" y2="3" />
		</svg>
	);
}

function IconTarget() {
	return (
		<svg
			aria-hidden
			width={16}
			height={16}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<circle cx="12" cy="12" r="10" />
			<circle cx="12" cy="12" r="6" />
			<circle cx="12" cy="12" r="2" />
		</svg>
	);
}

function IconCheck() {
	return (
		<svg
			aria-hidden
			width={16}
			height={16}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<polyline points="20 6 9 17 4 12" />
		</svg>
	);
}

function IconTrending() {
	return (
		<svg
			aria-hidden
			width={16}
			height={16}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
			<polyline points="17 6 23 6 23 12" />
		</svg>
	);
}

function IconDollar() {
	return (
		<svg
			aria-hidden
			width={16}
			height={16}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<line x1="12" y1="1" x2="12" y2="23" />
			<path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
		</svg>
	);
}

function KpiTile({
	label,
	value,
	icon,
	testId,
}: {
	label: string;
	value: string;
	icon: React.ReactNode;
	testId: string;
}) {
	return (
		<div
			data-testid={testId}
			className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm"
		>
			<div className="flex items-center justify-between">
				<div className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					{label}
				</div>
				<div className="flex h-8 w-8 items-center justify-center rounded-md bg-green-50 text-green-700">
					{icon}
				</div>
			</div>
			<div className="mt-3 text-3xl font-semibold tabular-nums text-zinc-900">
				{value}
			</div>
		</div>
	);
}

function BarChart({ rows }: { rows: GoalReportRow[] }) {
	if (rows.length === 0) {
		return (
			<div
				className="py-8 text-center text-sm text-zinc-500"
				data-testid="goal-reports-chart-empty"
			>
				No data to chart.
			</div>
		);
	}
	// Sort + trim so the chart stays readable even for long lists.
	const sorted = [...rows]
		.sort((a, b) => b.pct_complete - a.pct_complete)
		.slice(0, 12);
	const w = 720;
	const h = 240;
	const padL = 160;
	const padR = 48;
	const padT = 12;
	const padB = 20;
	const chartW = w - padL - padR;
	const chartH = h - padT - padB;
	const maxVal = Math.max(100, ...sorted.map((r) => r.pct_complete));
	const barH = Math.max(12, Math.floor(chartH / sorted.length) - 6);
	const step = chartH / sorted.length;
	return (
		<div className="overflow-x-auto">
			<svg
				width={w}
				height={h}
				viewBox={`0 0 ${w} ${h}`}
				role="img"
				aria-label="Completion rate by goal"
				data-testid="goal-reports-chart"
				className="block"
			>
				<line
					x1={padL}
					y1={padT}
					x2={padL}
					y2={h - padB}
					stroke="#e4e4e7"
					strokeWidth={1}
				/>
				<line
					x1={padL}
					y1={h - padB}
					x2={w - padR}
					y2={h - padB}
					stroke="#e4e4e7"
					strokeWidth={1}
				/>
				{sorted.map((r, i) => {
					const y = padT + i * step + (step - barH) / 2;
					const barW = (Math.min(r.pct_complete, maxVal) / maxVal) * chartW;
					const fill =
						r.status === "complete" || r.status === "on_track"
							? "#16a34a"
							: r.status === "at_risk"
								? "#f59e0b"
								: "#e11d48";
					return (
						<g key={r.goal_id}>
							<title>{`${r.name}: ${fmtPct(r.pct_complete)}`}</title>
							<text
								x={padL - 8}
								y={y + barH / 2}
								textAnchor="end"
								dominantBaseline="middle"
								fontSize={11}
								fill="#3f3f46"
							>
								{r.name.length > 22
									? `${r.name.slice(0, 22)}\u2026`
									: r.name}
							</text>
							<rect
								x={padL}
								y={y}
								width={barW}
								height={barH}
								rx={3}
								ry={3}
								fill={fill}
								opacity={0.9}
							/>
							<text
								x={padL + barW + 6}
								y={y + barH / 2}
								dominantBaseline="middle"
								fontSize={11}
								fill="#18181b"
								className="tabular-nums"
							>
								{fmtPct(r.pct_complete)}
							</text>
						</g>
					);
				})}
			</svg>
		</div>
	);
}

export function GoalReportsPage() {
	const [from, setFrom] = useState<string>(daysAgoIso(30));
	const [to, setTo] = useState<string>(todayIso());
	const [metric, setMetric] = useState<MetricFilter>("all");
	const [scope, setScope] = useState<ScopeFilter>("all");
	const [activeOnly, setActiveOnly] = useState<boolean>(false);

	const queryArgs = {
		metric,
		scope,
		active_now: activeOnly,
		limit: 200,
	};
	const q = useGoalReportsQuery(queryArgs);
	const rows: GoalReportRow[] = q.data?.items ?? [];

	const tiles = useMemo(() => {
		if (rows.length === 0) {
			return {
				goalsTracked: 0,
				completions: 0,
				cvr: 0,
				revenue: 0,
			};
		}
		let completions = 0;
		let revenue = 0;
		let pctSum = 0;
		for (const r of rows) {
			if (r.metric === "conversions") completions += r.actual_total;
			if (r.metric === "revenue") revenue += r.actual_total;
			pctSum += r.pct_complete;
		}
		const cvr = pctSum / rows.length;
		return {
			goalsTracked: rows.length,
			completions,
			cvr,
			revenue,
		};
	}, [rows]);

	function handleExport() {
		if (rows.length === 0) return;
		const csv = rowsToCsv(rows);
		downloadCsv(`goal-reports-${from}-to-${to}.csv`, csv);
	}

	const hasError = Boolean(q.error);

	return (
		<main
			data-testid="goal-reports-page"
			className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-6"
		>
			{/* Topbar */}
			<section
				className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-white px-5 py-4 shadow-sm"
				aria-label="Report controls"
			>
				<div className="flex flex-col gap-1">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						Report
					</p>
					<h1 className="text-2xl font-semibold text-zinc-900">
						Goal Reports
					</h1>
				</div>
				<div className="flex flex-wrap items-end gap-3">
					<div>
						<label
							htmlFor="gr-from"
							className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500"
						>
							<IconCalendar /> From
						</label>
						<input
							id="gr-from"
							type="date"
							value={from}
							onChange={(e) => setFrom(e.target.value)}
							className="mt-1 w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
							data-testid="gr-from"
						/>
					</div>
					<div>
						<label
							htmlFor="gr-to"
							className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-zinc-500"
						>
							<IconCalendar /> To
						</label>
						<input
							id="gr-to"
							type="date"
							value={to}
							onChange={(e) => setTo(e.target.value)}
							className="mt-1 w-40 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
							data-testid="gr-to"
						/>
					</div>
					<button
						type="button"
						onClick={handleExport}
						disabled={rows.length === 0}
						data-testid="gr-export-csv"
						className="inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
					>
						<IconDownload /> Export CSV
					</button>
				</div>
			</section>

			{/* KPI tiles */}
			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
				aria-label="KPIs"
			>
				<KpiTile
					label="Goals tracked"
					value={fmtInt(tiles.goalsTracked)}
					icon={<IconTarget />}
					testId="gr-tile-goals"
				/>
				<KpiTile
					label="Completions"
					value={fmtInt(tiles.completions)}
					icon={<IconCheck />}
					testId="gr-tile-completions"
				/>
				<KpiTile
					label="Avg completion"
					value={fmtPct(tiles.cvr)}
					icon={<IconTrending />}
					testId="gr-tile-cvr"
				/>
				<KpiTile
					label="Revenue goal"
					value={fmtCurrency(tiles.revenue)}
					icon={<IconDollar />}
					testId="gr-tile-revenue"
				/>
			</section>

			{/* Filter bar */}
			<section
				className="flex flex-wrap items-end gap-4 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm"
				aria-label="Filters"
			>
				<div>
					<label
						htmlFor="gr-metric"
						className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500"
					>
						Goal type
					</label>
					<select
						id="gr-metric"
						value={metric}
						onChange={(e) => setMetric(e.target.value as MetricFilter)}
						className="mt-1 w-44 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
						data-testid="gr-metric"
					>
						<option value="all">All metrics</option>
						<option value="conversions">Conversions</option>
						<option value="revenue">Revenue</option>
						<option value="clicks">Clicks</option>
					</select>
				</div>
				<div>
					<label
						htmlFor="gr-scope"
						className="block text-[11px] font-medium uppercase tracking-wider text-zinc-500"
					>
						Scope
					</label>
					<select
						id="gr-scope"
						value={scope}
						onChange={(e) => setScope(e.target.value as ScopeFilter)}
						className="mt-1 w-44 rounded-md border border-zinc-200 px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
						data-testid="gr-scope"
					>
						<option value="all">All scopes</option>
						<option value="global">Global</option>
						<option value="campaign">Campaign</option>
						<option value="affiliate">Affiliate</option>
					</select>
				</div>
				<label
					className="flex cursor-pointer items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700"
					data-testid="gr-active-toggle"
				>
					<input
						type="checkbox"
						checked={activeOnly}
						onChange={(e) => setActiveOnly(e.target.checked)}
						className="accent-green-600"
						aria-label="Active goals only"
					/>
					Active now only
				</label>
			</section>

			{/* Breakdown table */}
			<section
				className="overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm"
				aria-label="Goal breakdown"
			>
				{q.isLoading ? (
					<p
						className="px-6 py-10 text-sm text-zinc-500"
						data-testid="goal-reports-loading"
					>
						Loading.
					</p>
				) : hasError ? (
					<p
						className="px-6 py-10 text-sm text-rose-600"
						role="alert"
						data-testid="goal-reports-error"
					>
						{q.error?.message ?? "Error"}
					</p>
				) : rows.length === 0 ? (
					<p
						className="px-6 py-10 text-sm text-zinc-500"
						data-testid="goal-reports-empty"
					>
						No goals in the selected range.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table
							className="w-full border-collapse text-sm"
							data-testid="goal-reports-table"
						>
							<thead className="bg-zinc-50">
								<tr>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
										Goal
									</th>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
										Metric
									</th>
									<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
										Completions
									</th>
									<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
										Rate
									</th>
									<th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-zinc-500">
										Target
									</th>
									<th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
										Status
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200">
								{rows.map((r) => {
									const isCurrency = r.metric === "revenue";
									const actualText = isCurrency
										? fmtCurrency(r.actual_total)
										: fmtInt(r.actual_total);
									const targetText = isCurrency
										? fmtCurrency(r.target_value)
										: fmtInt(r.target_value);
									return (
										<tr
											key={r.goal_id}
											data-testid={`goal-reports-row-${r.goal_id}`}
											className="transition-colors hover:bg-green-50/40"
										>
											<td className="px-4 py-3 text-zinc-900">
												<div className="font-medium">{r.name}</div>
												<div className="text-xs text-zinc-500">
													{r.days_remaining > 0
														? `${r.days_remaining}d remaining`
														: "Period ended"}
												</div>
											</td>
											<td className="px-4 py-3 text-zinc-700">
												{METRIC_LABEL[r.metric]}
											</td>
											<td className="px-4 py-3 text-right tabular-nums text-zinc-900">
												{actualText}
											</td>
											<td className="px-4 py-3 text-right tabular-nums text-zinc-900">
												{fmtPct(r.pct_complete)}
											</td>
											<td className="px-4 py-3 text-right tabular-nums text-zinc-700">
												{targetText}
											</td>
											<td className="px-4 py-3">
												<span
													className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
														STATUS_CHIP[r.status]
													}`}
													data-testid={`goal-reports-status-${r.goal_id}`}
												>
													{STATUS_LABEL[r.status]}
												</span>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{/* Chart */}
			<section
				className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm"
				aria-label="Completion rate by goal"
			>
				<div className="mb-3 flex items-center justify-between">
					<h2 className="text-sm font-semibold text-zinc-900">
						Completion rate by goal
					</h2>
					<span className="text-xs text-zinc-500">Top 12</span>
				</div>
				{q.isLoading ? (
					<p
						className="py-8 text-center text-sm text-zinc-500"
						data-testid="goal-reports-chart-loading"
					>
						Loading.
					</p>
				) : hasError ? (
					<p
						className="py-8 text-center text-sm text-rose-600"
						role="alert"
						data-testid="goal-reports-chart-error"
					>
						{q.error?.message ?? "Error"}
					</p>
				) : (
					<BarChart rows={rows} />
				)}
			</section>
		</main>
	);
}

export default GoalReportsPage;
